import { useCallback, useRef, useMemo } from 'react'
import { shallowEqual, useSelector } from 'react-redux'

export const useDebouncedCallback = (callback, delay = 500) => {
	const timeoutRef = useRef(undefined)

	return useCallback(
		(...args) => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current)
			}
			timeoutRef.current = window.setTimeout(() => {
				callback(...args)
			}, delay)
		},
		[callback, delay]
	)
}

export const useCoupleHasPremium = (brideTier = null, groomTier = null) => {
	const accountType = useSelector((s) => s.auth.accountType)
	const isLifetimePlan = useSelector((s) =>
		Boolean(s.auth.user?.is_purchase_lifetime_plan) || s.auth.user?.payment !== 'free-tier'
	)
	const payment = useSelector((s) => s.auth.user?.payment)
	const paymentExpiresAt = useSelector((s) => s.auth.user?.paymentExpiresAt)
	
	const kazooMembers = useSelector((s) => s.kazooEvent.kazooMembers) || {}
	const bridePaymentExpiresAt = kazooMembers?.bride?.paymentExpiresAt
	const groomPaymentExpiresAt = kazooMembers?.groom?.paymentExpiresAt

	const currentUserHasPremium = useMemo(() => {
		if (isLifetimePlan) {
			return true
		}
		
		const today = new Date()
		if (payment !== 'free-tier' && paymentExpiresAt) {
			const endDate = new Date(paymentExpiresAt)
			if (endDate > today) {
				return true
			}
		}
		
		return false
	}, [accountType, isLifetimePlan, payment, paymentExpiresAt])

	const legacyHasPremium = useMemo(() => {
		const today = new Date()
		
		const hasValidTier = (tier, expiresAt) => {
			if (['premium-wedding-access-month', 'premium-wedding-access'].includes(tier)) {
				return true
			}
			
			if (expiresAt) {
				const endDate = new Date(expiresAt)
				return endDate > today
			}
			
			return false
		}
		
		const brideHasPremium = hasValidTier(brideTier, bridePaymentExpiresAt)
		const groomHasPremium = hasValidTier(groomTier, groomPaymentExpiresAt)
		
		return brideHasPremium || groomHasPremium
	}, [brideTier, bridePaymentExpiresAt, groomTier, groomPaymentExpiresAt])

	return useMemo(
		() => currentUserHasPremium || legacyHasPremium,
		[currentUserHasPremium, legacyHasPremium]
	)
}

const getMaxMedia = (tier) => {
	const map = {
		'business-basic-month': 20,
		'business-basic': 20,
		'business-pro-month': 40,
		'business-pro': 40,
	}
	return map[tier] || 1
}

export const useVendorOrPlannerSubscriptionAccess = () => {
	const accountType = useSelector((s) => s.auth.accountType)
	const paymentExpiresAt = useSelector((s) => s.auth.user?.paymentExpiresAt)
	const topBadge = useSelector(
		(s) => s.auth.user?.topVendor || s.auth.user?.topPlanner
	)
	const payment = useSelector((s) => s.auth.user?.payment)

	return useMemo(() => {
		if (!['vendor', 'kazoo-planner'].includes(accountType)) return null

		const today = new Date()
		const isPaymentExpired = paymentExpiresAt 
			? new Date(paymentExpiresAt) <= today 
			: true
		const hasValidPayment = ['business-basic-month', 'business-pro-month'].includes(payment) && 
			!isPaymentExpired

		let maxMedia = 1
		if (hasValidPayment) {
			maxMedia = getMaxMedia(payment)
		}

		if (topBadge && isPaymentExpired) {
			maxMedia = 10
		}

		return {
			accountType,
			topBadge,
			maxMedia,
			hasValidLegacyPayment: hasValidPayment,
			payment,
			paymentExpiresAt,
			isPaymentExpired,
			isSubscriptionExpired: isPaymentExpired,
		}
	}, [accountType, topBadge, paymentExpiresAt, payment])
}
